import { useProduct } from '@/hooks/api/useProducts'
import type { NormalizedCollection, ProductDto } from '@wtf/shared'
import { transformSmall, transformMedium } from '@wtf/shared'
import { replaceImageDomain } from '@/utils/replaceImageDomain'

// Default cover images that trigger the product image
const DEFAULT_COVER_URLS = [
    'https://r2.wtf.style/random%20colour-01.png',
    'https://r2.wtf.style/random%20colour-02.png',
    'https://r2.wtf.style/random%20colour-03.png',
    'https://r2.wtf.style/random%20colour-04.png',
    'https://r2.wtf.style/random%20colour-05.png',
    'https://r2.wtf.style/random%20colour-06.png',
    'https://r2.whatthefuckareyouwearing.com/random%20colour-01.png',
    'https://r2.whatthefuckareyouwearing.com/random%20colour-02.png',
    'https://r2.whatthefuckareyouwearing.com/random%20colour-03.png',
    'https://r2.whatthefuckareyouwearing.com/random%20colour-04.png',
    'https://r2.whatthefuckareyouwearing.com/random%20colour-05.png',
    'https://r2.whatthefuckareyouwearing.com/random%20colour-06.png',
]

/**
 * Check if the cover URL is a default cover (should show product image instead)
 */
const isDefaultCover = (url: string | null | undefined): boolean =>
    !url || DEFAULT_COVER_URLS.some((d) => url.includes(d))

/**
 * Get the first image URL from a product
 */
const getProductImageUrl = (product: ProductDto | undefined): string | null => {
    if (!product?.images?.length) return null
    const url = transformMedium(product.images[0].url) || product.images[0].url
    return replaceImageDomain(url)
}

interface CollectionImageProps {
    collection: NormalizedCollection
}

export function CollectionImage({ collection }: CollectionImageProps) {
    const firstProductId = collection.productIds?.[0]
    const shouldShowProductImage =
        isDefaultCover(collection.coverImageUrl) && !!firstProductId

    // Only fetch product if we need to show product image
    const { data: firstProduct } = useProduct(
        shouldShowProductImage ? firstProductId : '',
        { refetchOnMount: false }
    )

    const productImageUrl = shouldShowProductImage && firstProduct
        ? getProductImageUrl(firstProduct)
        : null

    const customCoverUrl = !shouldShowProductImage && collection.coverImageUrl
        ? replaceImageDomain(
            transformSmall(collection.coverImageUrl) ||
            collection.coverImageUrl
        )
        : null

    const imageUrl = productImageUrl || customCoverUrl

    if (!imageUrl) {
        return (
            <div className="h-full w-full bg-gray-200" />
        )
    }

    return (
        <img
            src={imageUrl}
            alt={collection.name}
            className="h-full w-full object-cover"
            referrerPolicy="no-referrer"
        />
    )
}
